import { endpoints } from './endpoints';
import {
    notifyJourney,
    getSessionId,
    getJwt,
    getRefreshToken,
    setJwt,
    setRefreshToken,
} from './journey';
import { stampLead } from './leadId';
import { ApiError, NetworkError } from './errors';

const isCryptoEnabled = process.env.REACT_APP_CRYPTO === 'on';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/**
 * Post-account-creation cross-sell layer (backend CrossSellCapabilities). Advertising
 * crosssell-v1 tells the backend this client renders the card container, so
 * /journey/status reports journey.crossSell instead of the legacy postEsignFno offer
 * once the layer is switched on. Sent on every request, token calls included, so a
 * refreshed or exchanged session never silently drops back to the legacy path.
 */
export const CAPABILITIES_HEADER = { 'X-Ekyc-Capabilities': 'crosssell-v1' };

/**
 * Silent JWT refresh (EW-I6224 / CC-4). On a 401 with `TOKEN_EXPIRED`, the
 * transport calls `POST /auth/refresh` with the stored (one-time-use) refresh
 * token, swaps in the new access JWT + rotated refresh token, and retries the
 * original request once — so an active user is never bounced to /sessionExpire
 * while a valid refresh token exists. Concurrent 401s share one in-flight
 * refresh (the refresh token is single-use, so parallel calls must not each
 * spend it). Returns true on success.
 */
let _refreshInFlight = null;
export const refreshAccessToken = () => {
    if (!_refreshInFlight) {
        _refreshInFlight = (async () => {
            const refreshToken = getRefreshToken();
            if (!refreshToken) return false;
            try {
                const res = await fetch(`${endpoints.baseUrl}${endpoints.stages.auth.refresh}`, {
                    method: 'POST',
                    headers: { ...JSON_HEADERS, ...CAPABILITIES_HEADER, ...(getSessionId() ? { 'X-Session-Id': getSessionId() } : {}) },
                    body: JSON.stringify({ refreshToken }),
                    credentials: 'omit',
                });
                const env = await parseJsonSafe(res);
                const ok = res.status >= 200 && res.status < 300
                    && env && env.success === true && env.data && env.data.jwt;
                if (!ok) return false;
                setJwt(env.data.jwt);
                if (env.data.refreshToken) setRefreshToken(env.data.refreshToken);
                return true;
            } catch (e) {
                return false;
            }
        })().finally(() => { _refreshInFlight = null; });
    }
    return _refreshInFlight;
};

/**
 * Zoho RM OTP-bypass handoff (TKT-0045, Flow B). Exchanges a single-use,
 * short-TTL `exchangeCode` (from `GET /handoff/resolve`) for a full session and
 * stores it exactly like a normal post-login: `POST /auth/exchange` is anonymous
 * and returns the same `RefreshResult` shape as `/auth/refresh`
 * ({ jwt, refreshToken, ... }). On success the new JWT + rotated refresh token
 * are persisted via setJwt/setRefreshToken and the caller can route into the
 * journey. Returns true on success, false otherwise (expired/used/invalid code →
 * the caller shows the session-expired screen). Mirrors refreshAccessToken().
 */
export const exchangeForSession = async (exchangeCode) => {
    if (!exchangeCode) return false;
    try {
        const res = await fetch(`${endpoints.baseUrl}${endpoints.stages.auth.exchange}`, {
            method: 'POST',
            headers: { ...JSON_HEADERS, ...CAPABILITIES_HEADER, ...(getSessionId() ? { 'X-Session-Id': getSessionId() } : {}) },
            body: JSON.stringify({ exchangeCode }),
            credentials: 'omit',
        });
        const env = await parseJsonSafe(res);
        const ok = res.status >= 200 && res.status < 300
            && env && env.success === true && env.data && env.data.jwt;
        if (!ok) return false;
        setJwt(env.data.jwt);
        if (env.data.refreshToken) setRefreshToken(env.data.refreshToken);
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Stage Completion Lock (2026-09) — called by the /multiSession page's Reload button.
 * Unlike refreshAccessToken/exchangeForSession, this call is AUTHENTICATED: the
 * current JWT is still fully valid (nothing in this design revokes it), and the
 * backend uses it only to identify which lead is asking. Reissues the JWT + refresh
 * token under the lead's CURRENT session id, so this device stops conflicting with
 * whichever device most recently advanced the journey. Returns true on success.
 */
export const resyncSession = async () => {
    const jwt = getJwt();
    if (!jwt) return false;
    try {
        const res = await fetch(`${endpoints.baseUrl}${endpoints.stages.auth.resync}`, {
            method: 'POST',
            headers: {
                ...JSON_HEADERS,
                ...CAPABILITIES_HEADER,
                Authorization: `Bearer ${jwt}`,
                ...(getSessionId() ? { 'X-Session-Id': getSessionId() } : {}),
            },
            credentials: 'omit',
        });
        const env = await parseJsonSafe(res);
        const ok = res.status >= 200 && res.status < 300
            && env && env.success === true && env.data && env.data.jwt;
        if (!ok) return false;
        setJwt(env.data.jwt);
        if (env.data.refreshToken) setRefreshToken(env.data.refreshToken);
        return true;
    } catch (err) {
        console.error('resyncSession failed:', err);
        return false;
    }
};

// Is this a 401 whose access token merely expired (refreshable), as opposed to
// a missing/malformed token (UNAUTHORIZED) or a dead session (SESSION_EXPIRED)?
const isRefreshable401 = (status, envelope) =>
    status === 401
    && envelope && envelope.errors && envelope.errors[0]
    && envelope.errors[0].code === 'TOKEN_EXPIRED';

/**
 * Build the headers stamped on every non-anonymous request:
 *   - Authorization: Bearer <jwt>   (if a JWT is currently held)
 *   - X-Session-Id : <sessionId>    (if a session has been initialised)
 *   - X-Ekyc-Capabilities: crosssell-v1 (always; see CAPABILITIES_HEADER)
 *
 * For anonymous endpoints (e.g. /journey/init, /security/public-key, /registration/signup),
 * pass `{ anonymous: true }` to the request to suppress JWT.
 */
const authHeaders = ({ anonymous } = {}) => {
    const h = { ...CAPABILITIES_HEADER };
    if (!anonymous) {
        const jwt = getJwt();
        if (jwt) h.Authorization = `Bearer ${jwt}`;
    }
    const sessionId = getSessionId();
    if (sessionId) h['X-Session-Id'] = sessionId;
    return h;
};

// GET /journey/status is the only envelope that computes `journey.postEsignFno`
// (backend: JourneyController; the eSign callback and the RiiSE handoff are the
// only other producers and are not routed through here). Every other endpoint
// leaves the field null, so only a snapshot from this URL is allowed to clear
// the stored value — see the postEsignFno branch in the journey reducer.
const JOURNEY_STATUS_RE = /\/journey\/status(\?|$)/;

/**
 * Unwrap the ApiResponse<T> envelope produced by the eKYC 3.0 backend:
 *   { success, message, data, correlationId, journey, errors }
 *
 * Side effect: if `journey` is non-null, dispatch JOURNEY_UPDATED.
 *
 * Returns: the inner `data` object on success.
 * Throws : ApiError on success: false (or on a non-2xx HTTP status).
 */
const unwrapEnvelope = (status, envelope, url) => {
    const isHttpOk = status >= 200 && status < 300;
    const isAppOk = envelope && envelope.success === true;
    // Only trust envelope.journey on a genuine success. Some rejection branches
    // (e.g. 409 LIVENESS.WRONG_STATE) stamp a hardcoded display journey snapshot
    // onto the error response that doesn't reflect the lead's real position —
    // dispatching it unconditionally poisons the journey reducer with a stage
    // the lead never actually reached.
    if (isHttpOk && isAppOk && envelope && envelope.journey) {
        notifyJourney(envelope.journey, {
            fnoAuthoritative: JOURNEY_STATUS_RE.test(String(url || '')),
            crossSellAuthoritative: JOURNEY_STATUS_RE.test(String(url || '')),
        });
    }
    if (isHttpOk && isAppOk) {
        return envelope.data;
    }
    throw new ApiError({
        message: (envelope && envelope.message) || `HTTP ${status}`,
        errors: envelope && envelope.errors,
        journey: envelope && envelope.journey,
        dropCode: envelope && envelope.journey && envelope.journey.dropCode,
        correlationId: envelope && envelope.correlationId,
        status,
        data: envelope && envelope.data,
    });
};

const parseJsonSafe = async (response) => {
    const text = await response.text();
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch (e) {
        // Backend should always return JSON; if it didn't, synthesise an envelope
        // so unwrap still produces an ApiError downstream.
        return {
            success: false,
            message: text,
            errors: [{ code: 'NON_JSON_RESPONSE', message: text }],
        };
    }
};

export default class Api {
    /**
     * Build a fully-qualified URL from a relative path. Backwards-compatible
     * with legacy `Api.buildUrl(path)` callers.
     */
    buildUrl(path) {
        return `${endpoints.baseUrl}${path}`;
    }

    /**
     * Core JSON request.
     *
     * Legacy signature preserved: api.fetch(url, method, bodyJsonString)
     *
     * Optional 4th arg lets callers opt into per-call behaviour:
     *   api.fetch(url, 'POST', JSON.stringify(payload), {
     *       anonymous: true,           // suppress Authorization header
     *       lead: { mode: 'body' },    // stamp leadId per the leadId helper
     *   })
     */
    async fetch(url, method, body, opts = {}) {
        let req = {
            url,
            method,
            headers: { ...JSON_HEADERS, ...authHeaders(opts) },
            body,
        };

        if (opts.lead) req = stampLead(req, opts.lead);

        // Encryption hook (Phase E). Off by default.
        if (isCryptoEnabled) {
            req = await this._encryptRequest(req);
        }

        let response;
        try {
            response = await fetch(req.url, {
                method: req.method,
                headers: req.headers,
                body: req.body,
                credentials: 'omit',
            });
        } catch (cause) {
            const netErr = new NetworkError({ cause, url: req.url });
            // Error catalogue (Phase 3): a fetch-level failure is frontend-raised
            // (GLOBAL_NETWORK_SD_FE_06). Backend-returned errors are recorded server-side
            // and are NOT reported here. Lazy import avoids a services/index <-> telemetry cycle.
            import('./errorReport').then((m) => m.reportNetworkError(netErr)).catch(() => { /* swallow */ });
            throw netErr;
        }

        let envelope = await parseJsonSafe(response);

        if (isCryptoEnabled) {
            envelope = await this._decryptResponse(envelope, req);
        }

        // EW-I6224: silent refresh + single retry on an expired access token.
        if (!opts.anonymous && !opts._isAuthRetry && getRefreshToken() && isRefreshable401(response.status, envelope)) {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
                return this.fetch(url, method, body, { ...opts, _isAuthRetry: true });
            }
        }

        return unwrapEnvelope(response.status, envelope, url);
    }

    /**
     * Binary GET — for endpoints that stream a file (e.g. the AOF PDF at
     * GET /documents/aof/download/{userId}) rather than a JSON envelope. Sends the
     * JWT like a normal request but returns the raw Blob; the caller turns it into
     * an object URL. Do NOT route file responses through `fetch()` — that assumes a
     * JSON envelope and would surface the raw bytes as a garbage error string.
     */
    async fetchBlob(url, method = 'GET', opts = {}) {
        let req = { url, method, headers: authHeaders(opts) };
        if (opts.lead) req = stampLead(req, opts.lead);
        let response;
        try {
            response = await fetch(req.url, { method: req.method, headers: req.headers, credentials: 'omit' });
        } catch (cause) {
            const netErr = new NetworkError({ cause, url: req.url });
            // Error catalogue (Phase 3): a fetch-level failure is frontend-raised
            // (GLOBAL_NETWORK_SD_FE_06). Backend-returned errors are recorded server-side
            // and are NOT reported here. Lazy import avoids a services/index <-> telemetry cycle.
            import('./errorReport').then((m) => m.reportNetworkError(netErr)).catch(() => { /* swallow */ });
            throw netErr;
        }
        // Retry once on a refreshable 401 (mirror fetch()'s silent-refresh path).
        if (!opts.anonymous && !opts._isAuthRetry && getRefreshToken() && response.status === 401) {
            const refreshed = await refreshAccessToken();
            if (refreshed) return this.fetchBlob(url, method, { ...opts, _isAuthRetry: true });
        }
        if (!(response.status >= 200 && response.status < 300)) {
            throw new ApiError({ message: `HTTP ${response.status}`, status: response.status });
        }
        return response.blob();
    }

    /**
     * Multipart upload. `body` is a FormData; do not stringify it. Encryption is
     * bypassed for multipart — file uploads stay raw, only the JSON channel is
     * encrypted.
     */
    async fetchFile(url, method, body, opts = {}) {
        // No Content-Type header — fetch sets the multipart boundary automatically.
        let req = {
            url,
            method,
            headers: authHeaders(opts),
            body,
        };

        if (opts.lead) req = stampLead(req, opts.lead);

        let response;
        try {
            response = await fetch(req.url, {
                method: req.method,
                headers: req.headers,
                body: req.body,
                credentials: 'omit',
            });
        } catch (cause) {
            const netErr = new NetworkError({ cause, url: req.url });
            // Error catalogue (Phase 3): a fetch-level failure is frontend-raised
            // (GLOBAL_NETWORK_SD_FE_06). Backend-returned errors are recorded server-side
            // and are NOT reported here. Lazy import avoids a services/index <-> telemetry cycle.
            import('./errorReport').then((m) => m.reportNetworkError(netErr)).catch(() => { /* swallow */ });
            throw netErr;
        }

        const envelope = await parseJsonSafe(response);

        // EW-I6224: silent refresh + single retry on an expired access token.
        if (!opts.anonymous && !opts._isAuthRetry && getRefreshToken() && isRefreshable401(response.status, envelope)) {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
                return this.fetchFile(url, method, body, { ...opts, _isAuthRetry: true });
            }
        }

        return unwrapEnvelope(response.status, envelope, url);
    }

    async _encryptRequest(req) {
        if (!Api._cryptoImpl) {
            throw new Error(
                'REACT_APP_CRYPTO=on but services/crypto.js is not wired up yet (Phase E)'
            );
        }
        return Api._cryptoImpl.encryptRequest(req);
    }

    async _decryptResponse(envelope, req) {
        if (!Api._cryptoImpl) return envelope;
        return Api._cryptoImpl.decryptResponse(envelope, req);
    }

    /**
     * Phase E will call Api.installCrypto({ encryptRequest, decryptResponse })
     * from src/services/crypto.js to wire encryption in.
     */
    static installCrypto(impl) {
        Api._cryptoImpl = impl;
    }
}

export { ApiError, NetworkError } from './errors';
export {
    setJwt,
    clearJwt,
    getJwt,
    setSessionId,
    getSessionId,
    setLeadId,
    getLeadId,
    getUserId,
    getJourney,
    routeForJourney,
} from './journey';
